import {
  Building2,
  ChevronDown,
  ChevronRight,
  Copy,
  MapPin,
  MoreHorizontal,
  Pencil,
  Plus,
  Trash2,
} from 'lucide-react';
import React, { useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { DeleteConfirmationDialog } from '@/components/ui/delete-confirmation-dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  type CarrierDivision,
  useListDivisionsQuery,
} from '@/services/api/carriersApiService';

import { HANDLE_TYPE_ICONS } from './constants';
import { ContactHandleRow } from './ContactHandleRow';
import { CreateDivisionModal } from './CreateDivisionModal';
import { DivisionHandlesInline } from './DivisionHandlesInline';
import { EditCarrierAddressModal } from './EditCarrierAddressModal';
import type { Carrier, ContactHandle } from './types';

// Helper to format carrier address
const formatCarrierAddress = (carrier: Carrier): string | null => {
  const parts: string[] = [];
  if (carrier.addressLine1) parts.push(carrier.addressLine1);
  if (carrier.addressLine2) parts.push(carrier.addressLine2);

  const cityStateZip: string[] = [];
  if (carrier.city) cityStateZip.push(carrier.city);
  if (carrier.state) cityStateZip.push(carrier.state);
  if (carrier.zip) cityStateZip.push(carrier.zip);

  if (cityStateZip.length > 0) {
    // Format as "City, State ZIP"
    const cityState = cityStateZip.slice(0, 2).join(', ');
    const formatted =
      cityStateZip.length === 3 ? `${cityState} ${cityStateZip[2]}` : cityState;
    parts.push(formatted);
  }

  return parts.length > 0 ? parts.join(', ') : null;
};

interface CarrierCardProps {
  carrier: Carrier;
  onAddHandle: (updates: Partial<ContactHandle>) => void;
  onUpdateHandle: (handleId: string, updates: Partial<ContactHandle>) => void;
  onDelete: (carrierId: string) => void;
}

// Quick copy button for handles
const QuickCopyButton = ({
  value,
  label,
  type,
}: {
  value: string;
  label: string;
  type: 'PHONE' | 'EMAIL' | 'FAX' | 'WEBSITE' | 'MAILING_ADDRESS';
}) => {
  const Icon = HANDLE_TYPE_ICONS[type];

  const handleCopy = async () => {
    await navigator.clipboard.writeText(value);
    toast.success(`${label} copied to clipboard`);
  };

  return (
    <button
      type="button"
      onClick={handleCopy}
      className="group flex items-center gap-2 px-3 py-2 bg-gray-50 hover:bg-primary/5 border border-gray-200 hover:border-primary/30 rounded-lg transition-all"
    >
      <Icon className="w-3.5 h-3.5 text-gray-400 group-hover:text-primary" />
      <span className="text-sm text-gray-700 group-hover:text-gray-900 truncate max-w-[180px]">
        {value}
      </span>
      <Copy className="w-3 h-3 text-gray-300 group-hover:text-primary opacity-0 group-hover:opacity-100 transition-opacity" />
    </button>
  );
};

export const CarrierCard = ({
  carrier,
  onAddHandle,
  onUpdateHandle,
  onDelete,
}: CarrierCardProps) => {
  const [isAddingHandle, setIsAddingHandle] = useState(false);
  const [isCreateDivisionModalOpen, setIsCreateDivisionModalOpen] =
    useState(false);
  const [isEditAddressModalOpen, setIsEditAddressModalOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [expandedDivision, setExpandedDivision] = useState<string | null>(null);
  const [showAllHandles, setShowAllHandles] = useState(false);
  const canEdit = true;
  const activeHandles = carrier.handles.filter((h) => !h.archivedAt);
  const formattedAddress = formatCarrierAddress(carrier);

  // Divisions
  const { data: divisions } = useListDivisionsQuery({ carrierId: carrier.id });

  const newHandle: ContactHandle = {
    id: 'new',
    value: '',
    handleType: 'PHONE',
    name: '',
    description: null,
    source: null,
    divisionId: null,
  };

  // Get quick-copy handles (first phone, fax, email)
  const quickCopyHandles = activeHandles.filter(
    (h) =>
      h.handleType !== 'MAILING_ADDRESS' &&
      h.handleType !== 'WEBSITE' &&
      h.value,
  );

  // Show new handle at the TOP when adding
  const displayHandles = isAddingHandle
    ? [newHandle, ...activeHandles]
    : activeHandles;

  const handleAddClick = () => {
    setIsAddingHandle(true);
    setShowAllHandles(true);
  };

  const handleCancelAdd = () => {
    setIsAddingHandle(false);
  };

  const handleNewHandleUpdate = (updates: Partial<ContactHandle>) => {
    onAddHandle(updates);
    setIsAddingHandle(false);
  };

  return (
    <div className="bg-white rounded-xl border border-gray-100 shadow-sm hover:shadow-md hover:border-gray-200 transition-all duration-200">
      {/* Header */}
      <div className="px-5 py-4 border-b border-gray-100">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-primary/10 to-primary/5 rounded-lg flex items-center justify-center">
              <Building2 className="w-5 h-5 text-primary" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                {carrier.name}
              </h3>
              {carrier.aliases && carrier.aliases.length > 0 && (
                <p className="text-xs text-gray-400">
                  Also known as: {carrier.aliases.slice(0, 2).join(', ')}
                  {carrier.aliases.length > 2 &&
                    ` +${carrier.aliases.length - 2} more`}
                </p>
              )}
              {formattedAddress && (
                <p className="text-xs text-gray-500 flex items-center gap-1 mt-1">
                  <MapPin className="w-3 h-3" />
                  {formattedAddress}
                </p>
              )}
            </div>
          </div>

          {canEdit && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={handleAddClick}>
                  <Plus className="mr-2 h-4 w-4" />
                  Add contact
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => setIsCreateDivisionModalOpen(true)}
                >
                  <Plus className="mr-2 h-4 w-4" />
                  Add division
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={() => setIsEditAddressModalOpen(true)}
                >
                  <Pencil className="mr-2 h-4 w-4" />
                  {formattedAddress ? 'Edit address' : 'Add address'}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={() => setIsDeleteDialogOpen(true)}
                  className="text-destructive focus:text-destructive"
                >
                  <Trash2 className="mr-2 h-4 w-4" />
                  Delete carrier
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>

      {/* Quick Copy Handles */}
      {quickCopyHandles.length > 0 && !showAllHandles && (
        <div className="px-5 py-3 flex flex-wrap gap-2">
          {quickCopyHandles.slice(0, 4).map((handle) => (
            <QuickCopyButton
              key={handle.id}
              value={handle.value || ''}
              label={handle.name || handle.handleType}
              type={handle.handleType}
            />
          ))}
          {activeHandles.length > quickCopyHandles.slice(0, 4).length && (
            <button
              type="button"
              onClick={() => setShowAllHandles(true)}
              className="px-3 py-2 text-sm text-primary hover:bg-primary/5 rounded-lg transition-colors"
            >
              +{activeHandles.length - quickCopyHandles.slice(0, 4).length} more
            </button>
          )}
        </div>
      )}

      {/* Expanded Handles Section */}
      {showAllHandles && (
        <div className="px-5 py-4 border-t border-gray-50 bg-gray-50/50">
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-sm font-medium text-gray-700">
              All Contact Methods
            </h4>
            <button
              type="button"
              onClick={() => setShowAllHandles(false)}
              className="text-xs text-gray-500 hover:text-gray-700"
            >
              Collapse
            </button>
          </div>
          <div className="space-y-2">
            {displayHandles.length === 0 ? (
              <p className="text-sm text-gray-500 italic py-2">
                No contact methods yet
              </p>
            ) : (
              displayHandles.map((handle) => (
                <ContactHandleRow
                  key={handle.id}
                  handle={handle}
                  startInEditMode={handle.id === 'new'}
                  onCancel={handle.id === 'new' ? handleCancelAdd : undefined}
                  onUpdate={
                    handle.id === 'new'
                      ? handleNewHandleUpdate
                      : (updates) => onUpdateHandle(handle.id, updates)
                  }
                  carrierId={carrier.id}
                  canEdit={canEdit}
                />
              ))
            )}
          </div>
        </div>
      )}

      {/* Divisions Section */}
      {divisions && divisions.length > 0 && (
        <div className="px-5 py-4 border-t border-gray-100">
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-sm font-medium text-gray-700">Divisions</h4>
            {canEdit && (
              <button
                type="button"
                onClick={() => setIsCreateDivisionModalOpen(true)}
                className="text-xs text-primary hover:text-primary-dark flex items-center gap-1"
              >
                <Plus className="w-3 h-3" />
                Add
              </button>
            )}
          </div>

          {/* Division List */}
          <div className="space-y-1">
            {(divisions || []).map((d: CarrierDivision) => (
              <div
                key={d.id}
                className="rounded-lg border border-gray-100 overflow-hidden"
              >
                <button
                  type="button"
                  onClick={() =>
                    setExpandedDivision((prev) => (prev === d.id ? null : d.id))
                  }
                  className="w-full flex items-center justify-between px-3 py-2 hover:bg-gray-50 transition-colors"
                  aria-label={
                    expandedDivision === d.id
                      ? 'Collapse division'
                      : 'Expand division'
                  }
                >
                  <div className="flex items-center gap-2">
                    {expandedDivision === d.id ? (
                      <ChevronDown className="w-4 h-4 text-gray-400" />
                    ) : (
                      <ChevronRight className="w-4 h-4 text-gray-400" />
                    )}
                    <span className="text-sm font-medium text-gray-900">
                      {d.divisionName}
                    </span>
                    {d.isDefault && (
                      <span className="text-[10px] uppercase tracking-wide bg-primary/10 text-primary px-1.5 py-0.5 rounded font-medium">
                        Default
                      </span>
                    )}
                  </div>
                </button>
                {expandedDivision === d.id && (
                  <div className="px-3 pb-3 pt-1 bg-gray-50/50">
                    <DivisionHandlesInline
                      carrierId={carrier.id}
                      divisionId={d.id}
                      divisionName={d.divisionName}
                      carrierHandles={carrier.handles}
                    />
                  </div>
                )}
              </div>
            ))}
            {(!divisions || divisions.length === 0) && (
              <p className="text-xs text-gray-400 py-1">No divisions yet</p>
            )}
          </div>
        </div>
      )}

      {/* Empty State for no handles and no divisions */}
      {activeHandles.length === 0 &&
        (!divisions || divisions.length === 0) &&
        !showAllHandles && (
          <div className="px-5 py-6 text-center">
            <p className="text-sm text-gray-400 mb-2">
              No contact information yet
            </p>
            {canEdit && (
              <div className="flex justify-center gap-2">
                <Button variant="outline" size="sm" onClick={handleAddClick}>
                  <Plus className="w-4 h-4 mr-1" />
                  Add contact
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setIsCreateDivisionModalOpen(true)}
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Add division
                </Button>
              </div>
            )}
          </div>
        )}

      {/* Create Division Modal */}
      <CreateDivisionModal
        open={isCreateDivisionModalOpen}
        onClose={() => setIsCreateDivisionModalOpen(false)}
        carrierId={carrier.id}
        carrierName={carrier.name}
        existingHandles={carrier.handles}
      />

      {/* Edit Address Modal */}
      <EditCarrierAddressModal
        open={isEditAddressModalOpen}
        onClose={() => setIsEditAddressModalOpen(false)}
        carrier={carrier}
      />

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmationDialog
        open={isDeleteDialogOpen}
        onOpenChange={setIsDeleteDialogOpen}
        onConfirm={() => onDelete(carrier.id)}
        title={`Delete ${carrier.name}?`}
        description="This will remove the carrier and all its contact information from the directory. This action cannot be undone."
        confirmText="Delete carrier"
      />
    </div>
  );
};
